import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render-time throw and shows what it was.
 *
 * Without one, React 19 unmounts the whole tree on an uncaught error: the
 * studio becomes a black page with no explanation, and the only way to learn
 * anything is to have had DevTools open at the moment it happened. That is a
 * poor trade in a tool used live, where reproducing the moment is expensive and
 * the director needs to know whether to reload or to leave.
 *
 * It deliberately prints the message and both stacks on screen rather than
 * hiding them behind a friendly sentence. This is a self-hosted studio operated
 * by the person who deploys it - the audience for a crash here is someone who
 * can act on it, and a stack they can copy is worth more than an apology.
 *
 * A boundary only sees errors thrown while rendering, in lifecycles, or in
 * constructors. Throws inside event handlers and promise rejections do not
 * reach it - React does not route them here - so this is a net, not a guarantee.
 */
interface ErrorBoundaryProps {
  /** Names the region in the heading, e.g. "studio". */
  label: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the console copy: it carries the original stack frames, which is
    // what a source map can still resolve after this component has rendered.
    console.error(`[${this.props.label}] render failed`, error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  override render() {
    const { error, componentStack } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div className="crash">
        <div className="crash__box">
          <h1>The {this.props.label} stopped</h1>
          <p className="muted">
            Something threw while drawing the page. Nothing has been sent to the audience since; the
            room connection is gone with the page. Copy this and reload.
          </p>

          <p className="alert alert--error crash__message">
            {error.name}: {error.message}
          </p>

          {error.stack ? (
            <pre className="crash__stack">
              <code>{error.stack}</code>
            </pre>
          ) : null}

          {componentStack ? (
            <pre className="crash__stack">
              <code>{componentStack.trim()}</code>
            </pre>
          ) : null}

          <div className="crash__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(
                  `${error.name}: ${error.message}\n\n${error.stack ?? ''}\n\n${componentStack ?? ''}`,
                );
              }}
            >
              Copy the report
            </button>
          </div>
        </div>
      </div>
    );
  }
}
